import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useWalletConnection } from "../../hooks/useWalletConnection";
import { useTermsAcceptance } from "../../hooks/useTermsAcceptance";
import { useIsMarketDisabled } from "../../hooks/useDisabledMarkets";
import { toast } from "react-toastify";
import {
  updateLiquidationPrice,
  calculateEnhancedLiquidation,
  fetchTradeDataWithLiquidation
} from "../../ReduxSlices/LiquidationSlice";
import { 
  executePerps, 
  queryContract, 
  queryBalance,
  getPerpBorrowingFees,
  calculateOpeningFee,
  calculateClosingFee
} from "../functions/Contract";
import { NUSDIcon, NibiruIcon } from "../../AssetComponents/Images";
import TradeTabs from "./TradeFormComponents/TabsComponent";
import TradeDirection from "./TradeFormComponents/TradeDirection";
import DepositAmount from "./TradeFormComponents/DepositAmountComponent";
import TPSLSection from "./TradeFormComponents/TakeProfitStopLoss";
import OrderConfirmModal from "./TradeFormComponents/OrderConfirmModal";
import { PurpleButton } from "../Buttons/PurpleButton";
import { ethers } from 'ethers';
import { CLIENT_ORIGIN } from "../../utils/clientOrigin";

// Import EVM trading services
import {
  fetchEvmTokenBalance,
  fetchCombinedBalance,
  openTradeWithEvm,
  closeTradeMarketEvm,
  updateTakeProfitEvm,
  updateStopLossEvm,
  updateLeverageEvm
} from "../../evm/services/tradingEvmService";
import { executeEvmPerps, queryEvmBalance } from "../../evm/services/EvmContract";
import { NIBIRU_EVM_CHAIN, PERP_VAULT_INTERFACE_ADDRESS } from "../../evm/EvmConstants";
import { EstimatedSlippage } from "./TradeFormComponents/EstimatedSlippage";
// The risk table the Pokédex dial is built on. The dial drawing itself is not
// used here any more — the ticket states leverage as a figure and a slider.
import { leverageRisk } from "../Cockpit/LeverageDial";
// The dial already ticks on drag and arrow keys; the slider and the shortcut
// buttons are the other two ways to change leverage and should feel the same.
import { hapticTick, hapticTap } from "../Cockpit/haptics";
import { useBlockchainStateChecker } from "../../hooks/useBlockchainStateChecker";
import { BlackButton } from "../Buttons/BlackButton";
import { WhiteButton } from "../Buttons/WhiteButton";
import { getMarketByTokenIndex, MARKET_CATEGORIES } from "../../config/markets";
import { resolveMaxLeverage } from "../../utils/leverageUtils";
import { isScheduledMarketOpen } from "../../config/tradingSchedules";
import { getNibiruSDK, getCollateralDenom, getContractAddresses, isTestnet } from "../../config/network";
import Modal from "react-responsive-modal";

import { api } from "../functions/Api";
import { enqueueTradeEvent } from "../../utils/trackingOutbox";
import { NumberTicker } from "./NumberAnimation";
import RangeSlider from "./TradeFormComponents/RangeSlider";

const nibiru = getNibiruSDK();

// Example function to get potential PnL difference
function getPnLDiff(entryPrice, targetPrice, isLong) {
  const e = parseFloat(entryPrice);
  const t = parseFloat(targetPrice);
  if (isNaN(e) || isNaN(t)) return 0;
  return isLong ? t - e : e - t;
}

// Helper to compute percentage difference based on entry price
function getPnLPercent(entryPrice, targetPrice, isLong) {
  const diff = getPnLDiff(entryPrice, targetPrice, isLong);
  const parsedEntry = parseFloat(entryPrice);
  if (isNaN(diff) || isNaN(parsedEntry) || parsedEntry === 0) return 0;
  return (diff / parsedEntry) * 100;
}

// Add helper to format numbers with thousand separators and prevent overflow
const formatNumber = (num) => {
  if (num == null || isNaN(num)) return "0.00";
  
  const numValue = Number(num);
  
  // Handle extremely large numbers (prevent overflow)
  // Max reasonable value: 1 trillion (1,000,000,000,000)
  const MAX_DISPLAY_VALUE = 1e12;
  
  if (Math.abs(numValue) >= MAX_DISPLAY_VALUE) {
    // Use scientific notation for extremely large numbers
    return numValue.toExponential(2);
  }
  
  // Handle very small numbers
  if (Math.abs(numValue) > 0 && Math.abs(numValue) < 0.000001) {
    return numValue.toExponential(4);
  }
  
  return numValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    // Allow up to 6 decimals so very small prices don't show as 0.00
    maximumFractionDigits: 6,
  });
};

// Low-price token IDs that need special formatting (CoinGecko-style with subscript)
const lowPriceTokenIds = new Set(["pump", "pengu", "bonk", "shib", "nibi"]);

const SUBSCRIPT_DIGITS = ["₀", "₁", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"];

const toSubscript = (n) =>
  String(n)
    .split("")
    .map((d) => SUBSCRIPT_DIGITS[Number(d)] || d)
    .join("");

// Format price for low-value tokens using CoinGecko-style notation.
// `asText` returns a plain string (unicode subscripts) for the places that put
// the price inside a template literal — toasts, aria labels — where the JSX
// form would stringify to "[object Object]".
const formatLowTokenPrice = (priceValue, isLowPriceToken, { asText = false } = {}) => {
  if (!priceValue && priceValue !== 0) return "0.00";

  const num = parseFloat(priceValue);
  if (isNaN(num)) return "0.00";

  const abs = Math.abs(num);

  // For low-price tokens < 1, show 0.0ₙddd style
  if (isLowPriceToken && abs > 0 && abs < 1) {
    const raw = abs.toFixed(20);
    const decimalPart = raw.split(".")[1] || "";

    let zeroCount = 0;
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] === "0") {
        zeroCount += 1;
      } else {
        break;
      }
    }

    const remaining = decimalPart.slice(zeroCount).slice(0, 3);

    if (!remaining) {
      return num.toFixed(8);
    }

    if (asText) {
      return `0.0${toSubscript(zeroCount)}${remaining}`;
    }

    return (
      <>
        0.0<sub>{zeroCount}</sub>{remaining}
      </>
    );
  }

  // For low-price tokens >= 1, show 6 decimals
  if (isLowPriceToken && abs >= 1) {
    return num.toFixed(6);
  }

  // Fallback to standard formatter
  return formatNumber(num);
};

const TradingForm = ({
  tokenIndex,
  marketIndex,
}) => {
  const { account, walletType, isConnected, isMetaMask, isCosmos, isEvm } = useWalletConnection();
  const { requireTermsAcceptance } = useTermsAcceptance();
  const bech32Address = useSelector((state) => state.auth.bech32Address);
  const perpsUpdate = useSelector((state) => state.trade.update);
  const dispatch = useDispatch();
  
  // Get market config for dynamic leverage
  const market = getMarketByTokenIndex(tokenIndex);
  const isLowPriceToken = market && lowPriceTokenIds.has(market.id);
  const staticMaxLeverage = market?.maxLeverage || 100; // Static fallback from config

  // Closed to new positions: all real estate, plus anything closed on-chain.
  const isMarketDisabled = useIsMarketDisabled(market);

  // Check if this market has scheduled trading hours (stocks)
  const [isMarketClosed, setIsMarketClosed] = useState(false);
  useEffect(() => {
    if (!market?.tradingSchedule) {
      setIsMarketClosed(false);
      return;
    }
    const check = () => setIsMarketClosed(!isScheduledMarketOpen(market.tradingSchedule));
    check();
    const id = window.setInterval(check, 5000); // check every 5s
    return () => window.clearInterval(id);
  }, [market?.tradingSchedule]);

  const [tab, setTab] = useState(0);
  const [longShort, setLongShort] = useState(0);
  // The ticket opens pre-filled with this so there is a position to price
  // immediately. It is only a placeholder — see the clamp effect below, which
  // trims it to the balance once that loads, so a trader holding less than
  // this is not greeted by "Insufficient USDC" over a number they never typed.
  const DEFAULT_DEPOSIT = 100;
  const [amount, setAmount] = useState(DEFAULT_DEPOSIT);
  const didClampDefaultAmount = useRef(false);
  const [availableAmount, setAvailableAmount] = useState(0);
  const [leverage_value, setLeverageValue] = useState(1.0);
  const [isMobile, setIsMobile] = useState(false);

  const [tokenBalance, setTokenBalance] = useState(0);
  const [erc20Balance, setErc20Balance] = useState(0);
  const [bankBalance, setBankBalance] = useState(0);
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  const [tp, setTp] = useState(null);
  const [sl, setSl] = useState(null);
  const [tpPercentInput, setTpPercentInput] = useState("");
  const [slPercentInput, setSlPercentInput] = useState("");
  const [openPrice, setOpenPrice] = useState(0);
  const [isManualTPSL, setIsManualTPSL] = useState(false);
  /**
   * The order ticket's two staged states.
   *
   * `showAdvanced` keeps take-profit, stop-loss and slippage folded away: a
   * market order needs none of them, and every trader who does not use them
   * was scrolling past three cards of controls to reach the submit button.
   *
   * `confirming` is the deliberate pause before an irreversible, funded
   * action. Submitting used to be a single click on a button whose label said
   * only "Market Buy/Long" — the same weight of gesture as changing a tab,
   * for the one interaction here that spends money. The button now opens a
   * summary of the order and asks to be held down.
   */
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Click-to-type on the leverage readout. The slider detents to whole
  // multiples and the shortcuts under it are fixed stops, so typing is the only
  // way to reach a value between them — 37x on a market whose ticks are
  // 1 · 25 · 50 · 75 · 100 is otherwise a drag-and-squint.
  const [levEditing, setLevEditing] = useState(false);
  const [levDraft, setLevDraft] = useState("");
  const [confirming, setConfirming] = useState(false);
  const currentPrice = useSelector((state) => state.trade.currentPrice);

  // Debug: Log price values
  useEffect(() => {
    if (currentPrice) {
      console.log("🔍 Current price from Redux:", {
        raw: currentPrice,
        type: typeof currentPrice,
        parsed: Number(currentPrice),
        tokenIndex,
        marketIndex
      });
    }
  }, [currentPrice, tokenIndex, marketIndex]);

  const [slippage, setSlippage] = useState(0.5);
  const [isAutoSlippage, setIsAutoSlippage] = useState(true);

  // Bridge help modals
  const [showUsdcBridgeModal, setShowUsdcBridgeModal] = useState(false);
  const [showNibiBridgeModal, setShowNibiBridgeModal] = useState(false);
  const [hideBridgeOnConnect, setHideBridgeOnConnect] = useState(false);
  const [bridgePrefsLoaded, setBridgePrefsLoaded] = useState(false);
  const bridgeFlowFromConnectRef = useRef(false);
  const prevIsConnectedRef = useRef(false);

  // Load "do not show again" preference for bridge modals
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("perp_hide_bridge_on_connect");
      if (stored === "true") {
        setHideBridgeOnConnect(true);
      }
    } catch (error) {
      console.error("Error reading bridge preference from localStorage:", error);
    } finally {
      setBridgePrefsLoaded(true);
    }
  }, []);

  // When wallet connection becomes true (and user has not opted out), show bridge flow
  useEffect(() => {
    if (!bridgePrefsLoaded) return;

    const wasConnected = prevIsConnectedRef.current;
    if (!wasConnected && isConnected && !hideBridgeOnConnect) {
      bridgeFlowFromConnectRef.current = true;
      setShowUsdcBridgeModal(true);
    }

    prevIsConnectedRef.current = isConnected;
  }, [isConnected, hideBridgeOnConnect, bridgePrefsLoaded]);

  const handleToggleHideBridgeOnConnect = useCallback(() => {
    setHideBridgeOnConnect((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(
          "perp_hide_bridge_on_connect",
          next ? "true" : "false"
        );
      } catch (error) {
        console.error("Error saving bridge preference to localStorage:", error);
      }
      return next;
    });
  }, []);

  // Fee-related state
  const [borrowingFees, setBorrowingFees] = useState(null);
  const [openingFee, setOpeningFee] = useState({ feeAmount: 0, feePercentage: 0 });
  const [closingFee, setClosingFee] = useState({ totalFee: 0, feePercentage: 0 });
  const [isLoadingFees, setIsLoadingFees] = useState(true);

  // Max leverage the UI offers. The contract wins: whatever the keeper reports
  // from the on-chain market config is what the bar runs to, even when it is
  // below the static entry in markets.js — a market capped at 50x on-chain must
  // not offer 100x, because the contract rejects it at open time. The config is
  // only the fallback while the keeper's response is in flight or unavailable.
  const maxLeverage = resolveMaxLeverage(borrowingFees?.maxLeverage, staticMaxLeverage);

  const { liquidationPrice, currentLeverage, isLoading: liquidationLoading } = useSelector(
    (state) => state.liquidation
  );
  const [lastWarningTime, setLastWarningTime] = useState(0);
  
  // Pre-format execution and liquidation prices
  // - Market (tab === 0): keep existing low-price formatting
  // - Limit (tab !== 0): show exactly 2 decimal places for a cleaner UX
  const rawExecutionPrice = tab === 0 ? currentPrice : openPrice;

  const formattedExecutionPrice =
    tab === 0
      ? formatLowTokenPrice(rawExecutionPrice, isLowPriceToken)
      : Number(rawExecutionPrice || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  // Liquidation price:
  // - Market: original behavior (low-price helper or 3 decimals)
  // - Limit: always 2 decimals
  let formattedLiquidationPrice;
  if (tab === 0) {
    if (isLowPriceToken) {
      formattedLiquidationPrice = formatLowTokenPrice(liquidationPrice, true);
    } else {
      const liqNum = Number(liquidationPrice || 0);
      if (Number.isNaN(liqNum)) {
        formattedLiquidationPrice = "0.000";
      } else {
        formattedLiquidationPrice = liqNum.toLocaleString("en-US", {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        });
      }
    }
  } else {
    const liqNum = Number(liquidationPrice || 0);
    formattedLiquidationPrice = Number.isNaN(liqNum)
      ? "0.00"
      : liqNum.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }

  // The low-price helper returns JSX for the subscript-zero notation, so the
  // string form is needed wherever the price goes into a template literal.
  const formattedLiquidationPriceText =
    tab === 0 && isLowPriceToken
      ? formatLowTokenPrice(liquidationPrice, true, { asText: true })
      : formattedLiquidationPrice;
  

  // Function to fetch borrowing fees
  const fetchBorrowingFees = useCallback(async () => {
    try {
      setIsLoadingFees(true);
      // Drop the previous market's data first: `maxLeverage` now comes from the
      // contract, so stale fees would briefly cap the bar at the old market's
      // limit while this fetch is in flight.
      setBorrowingFees(null);
      
      // Extract market ID and collateral ID from props
      const marketId = typeof marketIndex === 'string' ? 
        parseInt(marketIndex.replace(/\D/g, '')) : marketIndex;
      // Collateral ID differs by network: 1 on mainnet, 3 on testnet
      const collateralId = isTestnet() ? 3 : 1;
      
      console.log("Fetching borrowing fees for:", { marketId, collateralId });
      
      const fees = await getPerpBorrowingFees(marketId, collateralId);
      if (fees) {
        setBorrowingFees(fees);
        // Clamp to the range the bar actually offers — the contract's max once
        // the keeper has answered, so a selection made against the config
        // fallback snaps down to what the contract will accept.
        const offeredMax = resolveMaxLeverage(fees.maxLeverage, staticMaxLeverage);
        if (Number(leverage_value) > offeredMax) {
          setLeverageValue(String(offeredMax));
        }
        console.log("[TradingForm] Borrowing fees fetched from API:", fees);
      } else {
        console.warn("[TradingForm] API returned null, no fee data available");
        setBorrowingFees(null);
      }
    } catch (error) {
      console.error("[TradingForm] Failed to fetch borrowing fees:", error);
      setBorrowingFees(null);
    } finally {
      setIsLoadingFees(false);
    }
  }, [marketIndex]);

  // Function to calculate and update fees
  const updateFees = useCallback(() => {
    console.log("Updating fees with:", { 
      borrowingFees, 
      amount, 
      leverage_value, 
      tab,
      hasBorrowingFees: !!borrowingFees
    });
    
    if (!borrowingFees || !amount || !leverage_value) {
      console.log("Missing data for fee calculation:", {
        borrowingFees: !!borrowingFees,
        amount: !!amount,
        leverage_value: !!leverage_value
      });
      setOpeningFee({ feeAmount: 0, feePercentage: 0 });
      setClosingFee({ totalFee: 0, feePercentage: 0 });
      return;
    }

    // Clamp values to prevent overflow
    const MAX_REASONABLE_AMOUNT = 1e9; // 1 billion USDC max
    const MAX_REASONABLE_LEVERAGE = 1000; // Safety limit
    const MAX_FEE_AMOUNT = 1e12; // 1 trillion max fee
    
    const collateralAmount = Math.min(Math.max(0, Number(amount) || 0), MAX_REASONABLE_AMOUNT);
    const leverage = Math.min(Math.max(1, Number(leverage_value) || 1), MAX_REASONABLE_LEVERAGE);
    const isLimitOrder = tab !== 0; // tab 0 is market order, others are limit

    console.log("Fee calculation inputs:", {
      collateralAmount,
      leverage,
      isLimitOrder,
      borrowingFees
    });

    // Calculate opening fee
    const openFee = calculateOpeningFee(
      collateralAmount,
      leverage,
      isLimitOrder,
      borrowingFees
    );
    // Clamp fee amount to prevent overflow
    const clampedOpenFee = {
      feeAmount: Math.min(Math.max(0, openFee.feeAmount || 0), MAX_FEE_AMOUNT),
      feePercentage: openFee.feePercentage || 0
    };
    setOpeningFee(clampedOpenFee);

    // Calculate closing fee
    const closeFee = calculateClosingFee(
      collateralAmount,
      leverage,
      borrowingFees
    );
    // Clamp fee amount to prevent overflow
    const clampedCloseFee = {
      totalFee: Math.min(Math.max(0, closeFee.totalFee || 0), MAX_FEE_AMOUNT),
      feePercentage: closeFee.feePercentage || 0
    };
    setClosingFee(clampedCloseFee);

    console.log("Fees calculated:", { openFee: clampedOpenFee, closeFee: clampedCloseFee });
  }, [borrowingFees, amount, leverage_value, tab]);

  // Calculate daily borrowing rate for display
  // Only the dominant OI side pays borrowing fees — use the direction-specific rate.
  // If both are 0 (balanced OI or no data), fall back to max of both so the user
  // still sees the market's current rate.
  const dailyBorrowingRate = useMemo(() => {
    if (!borrowingFees || !amount || !leverage_value) return null;
    const positionSize = Number(amount) * Number(leverage_value);
    if (!positionSize || positionSize <= 0) return null;
    const longRate = Number(borrowingFees.feesPerHourLong || 0);
    const shortRate = Number(borrowingFees.feesPerHourShort || 0);
    const hourlyRate = longShort === 0 ? longRate : shortRate;
    // If the user's side pays 0, show that honestly — but also expose the
    // market rate so the component can display it as context.
    const marketHourlyRate = Math.max(longRate, shortRate);
    const dailyRate = hourlyRate * 24;
    const dailyCost = positionSize * dailyRate;
    const dailyRatePct = dailyRate * 100;
    const marketDailyRatePct = marketHourlyRate * 24 * 100;
    const marketDailyCost = positionSize * marketHourlyRate * 24;
    return { dailyCost, dailyRatePct, marketDailyCost, marketDailyRatePct, paysSide: hourlyRate > 0 };
  }, [borrowingFees, amount, leverage_value, longShort]);

  // High borrowing rate toast — alert once per hour if daily cost > $1
  const lastBorrowingToastRef = useRef(0);
  useEffect(() => {
    if (!borrowingFees || !dailyBorrowingRate) return;
    // Only alert when the user's side actually pays borrowing
    if (!dailyBorrowingRate.paysSide) return;

    const checkBorrowingRate = () => {
      const now = Date.now();
      if (
        dailyBorrowingRate.dailyCost > 1 &&
        now - lastBorrowingToastRef.current > 60 * 60 * 1000
      ) {
        lastBorrowingToastRef.current = now;
        toast.warning(
          `Daily borrowing cost is $${dailyBorrowingRate.dailyCost.toFixed(2)} (${dailyBorrowingRate.dailyRatePct.toFixed(4)}%/day). Keep track of your borrowing fees.`,
          { autoClose: 8000 }
        );
      }
    };

    checkBorrowingRate();
    const interval = setInterval(checkBorrowingRate, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dailyBorrowingRate, borrowingFees]);

  // Function to fetch balance, can be called after transactions
  const fetchBalance = useCallback(async () => {
    if (!account) {
      setTokenBalance(0);
      setIsLoadingBalance(false);
      return;
    }
    
    try {
      setIsLoadingBalance(true);
      
      console.log("Fetching balance for account:", account, "walletType:", walletType);
      
      // ✅ Route based on wallet type from the hook
      if (isEvm) {
        // EVM wallet (MetaMask, Bybit) — fetch ERC20 + bank combined
        console.log("Fetching combined balance for wallet:", walletType);
        const { erc20, bank, total } = await fetchCombinedBalance(account, bech32Address);
        console.log("Combined balance:", { erc20, bank, total });
        setErc20Balance(erc20);
        setBankBalance(bank);
        setTokenBalance(total);

      } else if (isCosmos) {
        // Cosmos wallet (Leap, Keplr)
        console.log("Fetching Cosmos balance for wallet:", walletType);
        const collateralDenom = getCollateralDenom();
        
        if (!collateralDenom) {
          throw new Error("Collateral denom not configured for current network");
        }
        
        console.log("Using collateral denom:", collateralDenom);
        const balance = await queryBalance(
          account,
          collateralDenom,
          nibiru.endptTm
        );
        
        console.log("Raw Cosmos balance result:", balance);
        
        // Handle case where balance might be undefined or have unexpected structure
        if (!balance || !balance.amount) {
          console.warn("Balance result is empty or invalid:", balance);
          setTokenBalance(0);
          return;
        }
        
        // Convert from micro-USDC to USDC (full precision, no rounding)
        const amountValue = Number(balance.amount) / 1e6;
        console.log("Cosmos balance (full precision):", amountValue);

        setTokenBalance(amountValue);
      } else {
        console.warn("Unknown wallet type:", walletType);
        throw new Error(`Unsupported wallet type: ${walletType}`);
      }
    } catch (err) {
      console.error("Failed to get balance during fetch operation:", err);
      console.error("Error details:", err.toString());
      setTokenBalance(0);
    } finally {
      setIsLoadingBalance(false);
    }
  }, [account, walletType, isEvm, isCosmos, bech32Address]);

  // Initial balance fetch on component mount.
  // bech32Address is in deps deliberately: for EVM wallets it's derived
  // asynchronously after `account` connects, and fetchCombinedBalance needs
  // it to include the internal "bank" balance alongside the ERC20 balance.
  // Without this, a fetch that ran before bech32Address resolved would
  // silently undercount to ERC20-only and never retry — "Insufficient USDC"
  // even though the combined balance covers the deposit.
  useEffect(() => {
    fetchBalance();
  }, [account, walletType, perpsUpdate, bech32Address]);

  // Trim the pre-filled DEFAULT_DEPOSIT down to what the trader actually holds,
  // once — the ticket used to open on 100 USDC regardless of balance, so anyone
  // holding less than that landed on a disabled "Insufficient USDC" button for a
  // figure they never entered, which reads as the balance being wrong rather
  // than the default being too big. Only the untouched default is replaced: a
  // number the trader typed is left alone (they may be about to bridge more in).
  useEffect(() => {
    if (didClampDefaultAmount.current) return;
    if (isLoadingBalance) return;

    const bal = Number(tokenBalance) || 0;
    if (bal <= 0) return; // nothing to clamp to yet

    didClampDefaultAmount.current = true;

    if (Number(amount) === DEFAULT_DEPOSIT && DEFAULT_DEPOSIT > bal) {
      // Floor rather than round, so the prefill can never exceed the balance.
      setAmount(Math.floor(bal * 100) / 100);
    }
  }, [isLoadingBalance, tokenBalance, amount]);

  // Refetch balance when bridge deposit/withdraw completes
  useEffect(() => {
    const handler = () => fetchBalance();
    window.addEventListener("bridge-balance-update", handler);
    return () => window.removeEventListener("bridge-balance-update", handler);
  }, [fetchBalance]);

  // Fetch borrowing fees on component mount
  useEffect(() => {
    fetchBorrowingFees();
  }, [marketIndex]);

  // Update fees when relevant parameters change
  useEffect(() => {
    updateFees();
  }, [borrowingFees, amount, leverage_value, tab]);

  useEffect(() => {
    const handleTradeUpdate = () => {
      console.log("Trade update event received, refreshing balance...");
      fetchBalance();
    };

    window.addEventListener('tradeUpdated', handleTradeUpdate);
    
    return () => {
      window.removeEventListener('tradeUpdated', handleTradeUpdate);
    };
  }, [account, walletType]);

  // Keep liquidation price preview in sync with the correct base price:
  // - Market orders (tab === 0): use live currentPrice
  // - Limit orders (tab !== 0): use user-entered openPrice when available
  useEffect(() => {
    const basePrice =
      tab === 0
        ? currentPrice
        : (openPrice && Number(openPrice) > 0 ? Number(openPrice) : currentPrice);

    if (basePrice) {
      dispatch(
        updateLiquidationPrice({
          price: basePrice,
          leverage: Number(leverage_value),
          collateral: Number(amount),
          isLong: longShort === 0,
        })
      );
    }
  }, [currentPrice, openPrice, leverage_value, amount, longShort, tab, dispatch]);

  useEffect(() => {
    if (tp && sl) {
      const currentTp = tp;
      const currentSl = sl;
      setTp(currentSl);
      setSl(currentTp);
    }
  }, [longShort]);

  const handlePercentageChange = (percentage) => {
    // Clamp percentage to 100 for calculation, but allow display of 100%+
    const clampedPercentage = Math.min(percentage, 100);
    const newAmount = ((clampedPercentage * tokenBalance) / 100).toFixed(2);
    setAmount(newAmount);
    // Show "100%+" if percentage is over 100, otherwise show the percentage
    if (percentage > 100) {
      setAvailableAmount("100+");
    } else {
      setAvailableAmount(percentage.toString());
    }
  };

  const handleUSDCAmountChange = (usdcAmount) => {
    const pct = (usdcAmount / tokenBalance) * 100;
    setAmount(usdcAmount);
    setAvailableAmount(pct.toFixed(2));
  };

  const handleIncrement = () => {
    const cur = parseFloat(availableAmount) || 0;
    const next = Math.min(100, cur + 1);
    setAvailableAmount(next.toString());
    setAmount(((next * tokenBalance) / 100).toFixed(2));
  };

  const handleDecrement = () => {
    const cur = parseFloat(availableAmount) || 0;
    const next = Math.max(0, cur - 1);
    setAvailableAmount(next.toString());
    setAmount(((next * tokenBalance) / 100).toFixed(2));
  };

  const diffTP = getPnLDiff(currentPrice || openPrice, tp, longShort === 0);
  const diffSL = getPnLDiff(currentPrice || openPrice, sl, longShort === 0);
  const percentTP = getPnLPercent(
    currentPrice || openPrice,
    tp,
    longShort === 0
  );
  const percentSL = getPnLPercent(
    currentPrice || openPrice,
    sl,
    longShort === 0
  );

  const handleTpChange = (newTp) => {
    setTp(newTp);
    const base = currentPrice || parseFloat(openPrice) || 0;
    if (base > 0) {
      const diff = parseFloat(newTp) - base;
      const newPercent = (diff / base) * 100;
      setTp(newTp);
    }
  };

  // Validate SL against liquidation price (called on blur or before submit)
  const validateStopLoss = (slValue) => {
    if (liquidationPrice > 0 && !isNaN(slValue) && slValue > 0) {
      if (longShort === 0) {
        // Long position: SL cannot be below liquidation price
        if (slValue < liquidationPrice) {
          toast.error(
            `Stop Loss cannot be below liquidation price ($${formattedLiquidationPriceText}).`,
            {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
          return false; // Invalid
        }
      } else {
        // Short position: SL cannot be above liquidation price
        if (slValue > liquidationPrice) {
          toast.error(
            `Stop Loss cannot be above liquidation price ($${formattedLiquidationPriceText}). The contract will reject orders beyond the -90% PnL threshold.`,
            {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
          return false; // Invalid
        }
      }
    }
    return true; // Valid
  };

  const handleSlChange = (newSl) => {
    // Allow typing - don't validate during input
    setSl(newSl);
    const base = currentPrice || parseFloat(openPrice) || 0;
    if (base > 0) {
      const diff = parseFloat(newSl) - base;
      const newPercent = (diff / base) * 100;
    }
  };

  // Handle SL blur - validate when user finishes typing
  const handleSlBlur = () => {
    if (sl) {
      const slValue = parseFloat(sl.toString().replace(/,/g, ''));
      validateStopLoss(slValue);
    }
  };

  const handleTpPercentChange = (percentStr) => {
    const percent = parseFloat(percentStr);
    const base = currentPrice || parseFloat(openPrice) || 0;
    if (!isNaN(percent) && base > 0) {
      // For Buy: Price increases by percentage
      // For Sell: Price decreases by percentage
      const multiplier =
        longShort === 0 ? 1 + percent / 100 : 1 - percent / 100;
      const newTp = (base * multiplier).toFixed(2);
      setTp(newTp);
    }
  };

  const handleSlPercentChange = (percentStr) => {
    const percent = parseFloat(percentStr);
    const base = currentPrice || parseFloat(openPrice) || 0;
    if (!isNaN(percent) && base > 0) {
      // For Buy: Price decreases by percentage
      // For Sell: Price increases by percentage
      const multiplier =
        longShort === 0 ? 1 - percent / 100 : 1 + percent / 100;
      const newSl = parseFloat((base * multiplier).toFixed(2));
      
      // Allow setting the value - validation will happen on blur or submit
      setSl(newSl.toFixed(2));
    }
  };

  // Update percentage input handlers
  const handleTpPercentInputChange = (value) => {
    setTpPercentInput(value);
    if (value === "") return;
    if (/^\d*\.?\d*$/.test(value)) {
      handleTpPercentChange(value);
    }
  };

  const handleSlPercentInputChange = (value) => {
    setSlPercentInput(value);
    if (value === "") return;
    if (/^\d*\.?\d*$/.test(value)) {
      handleSlPercentChange(value);
    }
  };

  useEffect(() => {
    // Only set the values if both TP and SL exist and are not empty
    if (tp && sl && Math.abs(percentTP) > 0 && Math.abs(percentSL) > 0) {
      setTpPercentInput(Math.abs(percentTP).toFixed(2));
      setSlPercentInput(Math.abs(percentSL).toFixed(2));
    } else {
      // Set to empty string to show the placeholder
      if (!tpPercentInput) setTpPercentInput("");
      if (!slPercentInput) setSlPercentInput("");
    }
  }, [percentTP, percentSL]);

  // Update the effect that swaps values when switching between Buy/Sell
  useEffect(() => {
    if (tp && sl) {
      const currentTp = tp;
      const currentSl = sl;
      // When switching modes, also validate the swapped values
      setTp(currentSl);
      setSl(currentTp);
    }
  }, [longShort]);

  const handleLeverageChange = (newValue) => {
    // Leverage is whole multiples only. Rounding here rather than in each
    // control means a fractional value cannot enter from any of them, or from
    // a restored draft.
    const actualValue = Math.round(Math.min(maxLeverage, Math.max(1, Number(newValue))));
    if (!isNaN(actualValue)) {
      setLeverageValue(actualValue.toString());

      // Use the same base price logic as the main liquidation effects
      const price =
        tab === 0
          ? currentPrice
          : (openPrice && Number(openPrice) > 0 ? Number(openPrice) : currentPrice);

      if (price) {
        dispatch(
          updateLiquidationPrice({
            price: price,
            leverage: actualValue,
            collateral: Number(amount) || 0,
            isLong: longShort === 0,
          })
        );
      }
    }
  };

  const handleSlippageChange = (value) => {
    // Convert value to string before checking if it ends with "."
    const valueStr = String(value);
    if (valueStr === "." || valueStr === "" || valueStr.slice(-1) === ".") {
      setSlippage(valueStr);
      return;
    }
    
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      setSlippage(0);
      return;
    }

    // Limit slippage to maximum 100%
    const limitedValue = Math.min(numericValue, 100);
    setSlippage(limitedValue);

    if (limitedValue > 5) {
      const currentTime = Date.now();
      if (currentTime - lastWarningTime >= 5000) {
        toast.warning(
          "Slippage has been set extremely high, we recommend using auto slippage for the optimal rate of slippage",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        setLastWarningTime(currentTime);
      }
    }
  };

  // Compute position size (ensure numeric conversion with overflow protection)
  const numericAmount = Number(amount);
  const numericLeverage = Number(leverage_value);
  
  // Validate and clamp values to prevent overflow
  const MAX_REASONABLE_AMOUNT = 1e9; // 1 billion USDC max
  const MAX_REASONABLE_LEVERAGE = 1000; // Safety limit even if market allows more
  const MAX_POSITION_SIZE = 1e12; // 1 trillion max position size
  
  const clampedAmount = Math.min(Math.max(0, numericAmount || 0), MAX_REASONABLE_AMOUNT);
  const clampedLeverage = Math.min(Math.max(1, numericLeverage || 1), MAX_REASONABLE_LEVERAGE);
  
  let positionSize = "0.00";
  if (!isNaN(clampedAmount) && !isNaN(clampedLeverage)) {
    const calculatedSize = clampedAmount * clampedLeverage;
    // Clamp position size to prevent overflow
    const finalSize = Math.min(calculatedSize, MAX_POSITION_SIZE);
    positionSize = finalSize.toFixed(2);
  }

  /**
   * What the submit button says, and whether it can be pressed.
   *
   * `handleOpenTrade` already refuses a disconnected wallet, an empty deposit
   * and a deposit larger than the balance — but it refused them on click, as a
   * red toast after the trader had committed to the action. The same three
   * conditions are read here so the button can name the one in the way before
   * it is pressed, and otherwise state the order itself: side, deposit and
   * leverage, the three things chosen above it.
   */
  const insufficientBalance = clampedAmount > 0 && clampedAmount > Number(tokenBalance || 0);
  const canSubmit = Boolean(account) && clampedAmount > 0 && !insufficientBalance;
  const submitLabel = !account
    ? "Connect Wallet"
    : clampedAmount <= 0
      ? "Enter an Amount"
      : insufficientBalance
        ? "Insufficient USDC"
        : `${longShort === 0 ? "Buy" : "Sell"} ${market?.symbol || ""} · $${formatNumber(String(clampedAmount))} · ${Math.round(clampedLeverage)}×`;

  /* A confirmation describes one specific order. Any edit above it — side,
     size, leverage, order type — makes the dialog stale, so it closes rather
     than confirming something the trader has since changed. */
  useEffect(() => { setConfirming(false); }, [amount, leverage_value, longShort, tab]);

  // MAIN TRADING FUNCTION - Routes to correct service based on wallet type
  const handleOpenTrade = async () => {
    try {
      if (!account) {
        toast.error("Please connect your wallet");
        return;
      }

      if (isMarketDisabled) {
        toast.error("This market is disabled. New positions can't be opened.");
        return;
      }

      // Defensive gate: ensure terms are accepted before trading
      if (!requireTermsAcceptance()) {
        return;
      }

      if (!walletType) {
        toast.error("Wallet type not detected. Please reconnect your wallet.");
        console.error("walletType is undefined. Hook returned:", { account, walletType });
        return;
      }

      console.log("Opening trade with:", { account, walletType, isEvm, isCosmos });

      const tradeAmountUSDC = Number(amount); // e.g., 100 USDC
      const slippageDecimal = (slippage / 100).toString();

      // Normalize price - ensure it's a valid number and properly formatted
      let normalizedPrice;
      if (tab === 0) {
        // Market order - use current price from oracle
        const priceNum = Number(currentPrice);
        if (isNaN(priceNum) || priceNum <= 0) {
          toast.error("Invalid current price. Please refresh and try again.");
          console.error("Invalid current price:", currentPrice);
          return;
        }
        normalizedPrice = priceNum.toString();
      } else {
        // Limit order - use user-specified price
        const priceNum = Number(openPrice);
        if (isNaN(priceNum) || priceNum <= 0) {
          toast.error("Invalid limit price. Please enter a valid price.");
          return;
        }
        normalizedPrice = priceNum.toString();
      }

      console.log("Price normalization:", {
        originalPrice: tab === 0 ? currentPrice : openPrice,
        normalizedPrice,
        tokenIndex,
        marketIndex
      });

      // Validate Stop Loss against liquidation price before submitting
      if (isManualTPSL && sl && liquidationPrice > 0) {
        const slValue = parseFloat(sl.toString().replace(/,/g, ''));
        if (!isNaN(slValue) && slValue > 0) {
          if (!validateStopLoss(slValue)) {
            return; // Don't submit trade if SL is invalid
          }
        }
      }

      // Collateral amount in micro-USDC (Uint128). Required by the on-chain
      // OpenTrade message. Ignored by the contract when funds are attached to
      // the tx (funds take precedence), used when trading from deposits.
      const collateralAmountMicroUSDC = Math.round(tradeAmountUSDC * 1000000).toString();

      // Common message structure for both EVM and Cosmos
      const message = {
        open_trade: {
          market_index: marketIndex,
          leverage: leverage_value.toString(),
          long: longShort === 0,
          collateral_index: process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL,
          trade_type: tab === 0 ? "trade" : "limit",
          open_price: normalizedPrice,
          tp: tp && isManualTPSL ? tp.toString().replace(/,/g, '') : undefined,
          sl: sl && isManualTPSL ? sl.toString().replace(/,/g, '') : undefined,
          slippage_p: slippageDecimal,
          is_evm_origin: isEvm ? true : false,
          client_origin: CLIENT_ORIGIN, // Public attribution: originating client
          collateral_amount: collateralAmountMicroUSDC
        },
      };

      // ✅ ROUTE TO CORRECT SERVICE BASED ON WALLET TYPE
      if (isEvm) {
        // EVM WALLET PATH (MetaMask, Bybit)
        console.log("Using EVM trading service for wallet:", walletType);
        
        const tokenToSend = [
          {
            amount: (tradeAmountUSDC * 1000000).toString(), // Convert to micro-USDC for consistency
            denom: getCollateralDenom() || "usdc"
          },
        ];

        const result = await executeEvmPerps(
          dispatch,
          message,
          account,
          tokenToSend
        );

        if (!result) {
          throw new Error("EVM transaction failed");
        }

        console.log("EVM trade completed with hash:", result);

        // Track successful trade event (mainnet only). Durable: persisted to the
        // outbox synchronously so it survives a tab close and is retried on failure.
        if (!isTestnet()) {
          enqueueTradeEvent({
            walletAddress: account,
            txHash: result,
            eventType: "trade_opened",
            tradeDetails: {
              marketIndex: marketIndex?.toString(),
              leverage: leverage_value?.toString(),
              isLong: longShort === 0,
              collateralAmount: tradeAmountUSDC?.toString(),
              tradeType: tab === 0 ? "trade" : "limit",
              openPrice: normalizedPrice,
              tp: tp && isManualTPSL ? tp.toString().replace(/,/g, '') : undefined,
              sl: sl && isManualTPSL ? sl.toString().replace(/,/g, '') : undefined,
            },
            walletType: walletType || "evm",
          });
        }

        // Refresh balance after successful trade
        setTimeout(async () => {
          await fetchBalance();
        }, 2000);

      } else if (isCosmos) {
        // 🌌 COSMOS WALLET PATH (Leap, Keplr)
        console.log("Using Cosmos trading service for wallet:", walletType);
        
        const depositAmountMicroUSDC = (tradeAmountUSDC * 1000000).toString(); // Convert to micro-USDC
        
        const tokenToSend = [
          {
            amount: depositAmountMicroUSDC,
            denom: getCollateralDenom()
          },
        ];

        console.log("Cosmos trade parameters:", {
          amount: tradeAmountUSDC,
          depositAmount: depositAmountMicroUSDC,
          leverage: leverage_value,
          message: message
        });

        const result = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType,
          tokenToSend
        );

        if (!result) {
          throw new Error("Cosmos transaction failed");
        }

        // 🔗 Transaction link already handled in executePerps function
        console.log("Cosmos trade completed with hash:", result);

        // Track successful trade event (mainnet only). Durable: persisted to the
        // outbox synchronously so it survives a tab close and is retried on failure.
        if (!isTestnet()) {
          enqueueTradeEvent({
            walletAddress: account,
            txHash: result,
            eventType: "trade_opened",
            tradeDetails: {
              marketIndex: marketIndex?.toString(),
              leverage: leverage_value?.toString(),
              isLong: longShort === 0,
              collateralAmount: tradeAmountUSDC?.toString(),
              tradeType: tab === 0 ? "trade" : "limit",
              openPrice: normalizedPrice,
              tp: tp && isManualTPSL ? tp.toString().replace(/,/g, '') : undefined,
              sl: sl && isManualTPSL ? sl.toString().replace(/,/g, '') : undefined,
            },
            walletType: walletType || "cosmos",
          });
        }

        // SMART TRIGGER: Only emit events, no polling
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('userTradeAction', {
            detail: { 
              txHash: result,
              tradeType: tab === 0 ? "market" : "limit",
              timestamp: Date.now()
            }
          }));
        }, 1000);

        // DELAYED CHECK: Trigger state verification after blockchain settlement
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('blockchainStateCheck', {
            detail: { account, timestamp: Date.now() }
          }));
        }, 8000); // Longer delay for Cosmos transactions
      } else {
        throw new Error(`Unsupported wallet type: ${walletType}`);
      }

      // Refresh balance after successful trade
      setTimeout(async () => {
        await fetchBalance();
      }, 2000);

      return true;
    } catch (err) {
      console.error("Open trade error:", err);
      // toast.error(`Failed to open trade: ${err.message}`);
      return false;
    }
  };

  // Ensure liquidation price is updated whenever core trade parameters change
  useEffect(() => {
    const price =
      tab === 0
        ? currentPrice
        : (openPrice && Number(openPrice) > 0 ? Number(openPrice) : currentPrice);

    if (price) {
      dispatch(
        updateLiquidationPrice({
          price: price,
          leverage: Number(leverage_value),
          collateral: Number(amount),
          isLong: longShort === 0,
        })
      );
    }
  }, [leverage_value, amount, longShort, currentPrice, openPrice, tab, dispatch]);

  // Use enhanced liquidation calculation with the same base price rule
  useEffect(() => {
    const price =
      tab === 0
        ? currentPrice
        : (openPrice && Number(openPrice) > 0 ? Number(openPrice) : currentPrice);

    if (price) {
      const numericLeverage = Number(leverage_value);
      const numericAmount = Number(amount);
      const isLong = longShort === 0;

      dispatch(calculateEnhancedLiquidation({
        price: price,
        leverage: numericLeverage,
        collateral: numericAmount,
        isLong: isLong,
        marketIndex: marketIndex,
        collateralIndex: process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL
      }));
    }
  }, [currentPrice, leverage_value, amount, longShort, openPrice, marketIndex, dispatch]);

  // ADD THIS: Enable blockchain state checking
  useBlockchainStateChecker();

  // Leverage shortcuts under the slider: 1×, quarters, max. De-duplicated and
  // sorted so low-max markets (e.g. POKE at 5×) don't repeat values.
  // Math.floor, not round — matches the marks the old rc-slider rendered, so a
  // 50x market still reads 1 · 12 · 25 · 37 · 50.
  const leverageTicks = Array.from(
    new Set([
      1,
      Math.floor(maxLeverage * 0.25),
      Math.floor(maxLeverage * 0.5),
      Math.floor(maxLeverage * 0.75),
      maxLeverage,
    ])
  )
    .filter((v) => v >= 1 && v <= maxLeverage)
    .sort((a, b) => a - b);

  // Value readout, slider and active tick all take their colour from the same
  // risk zone, so the control reads as one thing rather than three.
  // `leverageRisk` is the risk table the Pokédex dial uses — see LeverageDial.
  // Re-keying the readout restarts the kick animation; re-applying a class that
  // is already present would be ignored.
  const leverageStep = Math.round(Number(leverage_value) || 1);
  const leverageZone = leverageRisk(Number(leverage_value) || 1, 1, maxLeverage);
  const leverageRiskColor = leverageZone.color;

  const beginLevEdit = () => { setLevDraft(String(leverageStep)); setLevEditing(true); };

  /**
   * Commit a typed leverage.
   *
   * An empty or unparseable field backs out to the current value rather than
   * dropping leverage to 1 — a trader who selects the field and tabs away has
   * not asked to change anything. `handleLeverageChange` does the clamping and
   * the rounding, so this only has to decide whether there is a number at all.
   */
  const commitLevEdit = () => {
    setLevEditing(false);
    const n = parseFloat(levDraft);
    if (!Number.isFinite(n)) return;
    const next = Math.round(Math.min(maxLeverage, Math.max(1, n)));
    if (next !== leverageStep) { hapticTap(); handleLeverageChange(next); }
  };

  return (
    <>
      {/* The ticket is one column at its natural height: header, direction
          switch, deposit, leverage, summary, TP/SL, then the submit button at
          the bottom of it. The rail around it (see CryptoTrading) is what
          scrolls, so the button is reached by scrolling down to it.

          A pinned-footer variant was tried — ticket at height 100% with only
          its middle scrolling — and reverted: the button stayed put while the
          form moved under it, which reads as a separate bar rather than the
          end of this form. */}
      <div className="w-full flex justify-center">
        <div
          className="ce-ticket w-full min-w-[300px] max-w-[400px] flex flex-col"
          style={{
            background: "var(--surface)",
            borderRadius: 20,
            padding: 14,
            boxShadow: "var(--card)",
            gap: 10,
          }}
        >
          {/* The submit and confirm fills live in classes for the same reason
              the direction switch's do: the dark-mode sweep in theme.css
              rewrites inline backgrounds to surface tokens with `!important`,
              which turned the one button on this ticket that spends money into
              another dark panel. A `background-image` paints over whatever
              `background-color` that sweep sets. */}
          <style>{`
            .ce-submit.is-long   { background-image: linear-gradient(180deg, #24A066 0%, #1F8B58 100%); box-shadow: 0 10px 24px rgba(31,139,88,.32), inset 0 1px 0 rgba(255,255,255,.28); color: #fff; }
            .ce-submit.is-short  { background-image: linear-gradient(180deg, #D93F44 0%, #C8353A 100%); box-shadow: 0 10px 24px rgba(200,53,58,.32), inset 0 1px 0 rgba(255,255,255,.28); color: #fff; }
            .ce-submit.is-off    { background-image: linear-gradient(180deg, #C6C9D2, #B7BBC6); box-shadow: none; color: #fff; }
            /* The disabled ramp is a light-mode value. Left alone it is a
               near-white slab on a #0D0F14 page — the brightest thing on the
               screen, for the one control that cannot be pressed. */
            html[data-theme="dark"] .ce-submit.is-off { background-image: linear-gradient(180deg, #343A46, #2A2F39); color: var(--ink-3); }
            .ce-submit.is-long:hover:not(.is-off)  { transform: translateY(-1px); box-shadow: 0 14px 30px rgba(31,139,88,.34), inset 0 1px 0 rgba(255,255,255,.28); }
            .ce-submit.is-short:hover:not(.is-off) { transform: translateY(-1px); box-shadow: 0 14px 30px rgba(200,53,58,.34), inset 0 1px 0 rgba(255,255,255,.28); }
            .ce-submit:active:not(.is-off) { transform: translateY(1px) scale(.985); }
            /* All labels one weight, all values another — the ticket reads as
               a form, not as a stack of unrelated boxes. */
            .ce-summary > * + * { border-top: 1px solid var(--line-2); }
            .ce-summary > * { padding: 9px 0; }
          `}</style>

          {/* Header line: what this ticket is, and the order type beside it.
              Each used to own a full row — a 48px tab rule, and nothing at all
              naming the market being traded. */}
          <div className="flex items-center justify-between" style={{ gap: 8, flex: "0 0 auto" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "General Sans", fontWeight: 600, fontSize: 15, color: "var(--ink, #101828)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {market?.symbol ? `${market.symbol}/USDC` : "Place Trade"}
              </div>
              <div style={{ fontFamily: "General Sans", fontSize: 11.5, color: "var(--ink-2, #667085)" }}>
                up to {maxLeverage}× leverage
              </div>
            </div>
            <TradeTabs tab={tab} setTab={setTab} />
          </div>

          {/* Buy/Sell */}
          <div style={{ flex: "0 0 auto" }}>
            <TradeDirection longShort={longShort} setLongShort={setLongShort} />
          </div>

          {/* The body of the ticket: deposit, leverage, summary, TP/SL. It
              groups them so the gap between blocks is stated once. */}
          <div
            className="flex flex-col"
            style={{ gap: 10 }}
          >

          {/* Deposit: amount, balance shares, and the position it sizes.
              A raised panel — `--pillow-sm`, no border. Nested corners step
              down from the card: 20 → 16. */}
          <div
            style={{
              borderRadius: 16,
              padding: 14,
              background: "var(--surface)",
              boxShadow: "var(--pillow-sm)",
              gap: 12,
              flex: "0 0 auto",
            }}
            className="flex flex-col"
          >
            {/* Deposit Amount */}
            <DepositAmount
              amount={amount}
              setAmount={setAmount}
              tokenBalance={tokenBalance}
              setAvailableAmount={setAvailableAmount}
              onPercent={handlePercentageChange}
              percent={availableAmount === "100+" ? 100 : Number(availableAmount) || 0}
              onBridgeClick={isEvm ? () => window.dispatchEvent(new CustomEvent("open-bridge-modal", { detail: { tab: "deposit" } })) : undefined}
            />

            {/* Position size sits with the deposit rather than three cards
                further down. It is the deposit answered back — the only number
                that changes when a share chip is tapped — and a trader sizing
                a position should not have to scroll to see what they sized.
                The duplicate row in the summary card below is gone. */}
            <div
              className="flex justify-between items-center"
              style={{
                height: 42,
                borderRadius: 12,
                padding: "0 14px",
                background: "var(--cat-accent-soft, rgba(91, 29, 238, .10))",
              }}
            >
              <span style={{ fontFamily: "General Sans", fontWeight: 500, fontSize: 13.5, color: "var(--ink-2, #667085)" }}>
                Position Size
              </span>
              <span style={{ fontFamily: "General Sans", fontWeight: 600, fontSize: 16, color: "var(--cat-accent, #AF52DE)" }}>
                <NumberTicker text={`$${formatNumber(positionSize)}`} duration={0.5} stagger={0.03} />
              </span>
            </div>
          </div>

          <div className="space-y-3">

            {/* Leverage.

                This was a 150px risk dial, a native range slider and a tick
                row stacked vertically — the tallest thing on the ticket by
                some way, for a control whose whole output is one integer. It
                also stated that integer three times over: card header, dial
                centre, risk badge.

                It is one header line and one slider now. The dial's risk
                reading survives as the chip beside the value and as the
                slider's own colour, which is the part a trader acts on; the
                gauge drawing of it was the expensive part. The card is flat —
                the ticket is already a stack of cards, and a gradient-and-
                shadow panel nested inside one read as a floating box rather
                than a section. */}
            <div style={{ borderRadius: 16, background: 'var(--surface)', boxShadow: 'var(--pillow-sm)', padding: '11px 13px 12px' }}>
              <style>
                {`
                  .ce-lev-tick {
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    padding: 2px 7px;
                    border-radius: 999px;
                    font-family: 'General Sans';
                    font-size: 11.5px;
                    font-variant-numeric: tabular-nums;
                    transition: color .15s, background-color .15s;
                  }
                  .ce-lev-tick:hover { background-color: var(--surface-3); }
                  /* The felt half of the haptic — navigator.vibrate is
                     Android-only, so on most machines hapticTick() is a silent
                     no-op and this kick is the whole response. It now rides the
                     value readout rather than the dial that used to be here. */
                  @keyframes ce-lev-kick {
                    0%   { transform: scale(1); }
                    40%  { transform: scale(1.06); }
                    100% { transform: scale(1); }
                  }
                  .ce-lev-kick { animation: ce-lev-kick .09s ease-out; display: inline-flex; align-items: center; gap: 7px; }
                  @media (prefers-reduced-motion: reduce) { .ce-lev-kick { animation: none; } }

                  /* The readout, at rest. The dashed underline is the whole
                     affordance — a bare bold number reads as output, and this
                     one is an input. */
                  .ce-lev-value {
                    font-family: 'General Sans';
                    font-weight: 700;
                    font-size: 19px;
                    line-height: 1.1;
                    cursor: text;
                    padding: 0 3px 1px;
                    border-radius: 6px;
                    border-bottom: 2px dashed var(--ink-4);
                    transition: background-color .15s, border-color .15s;
                  }
                  .ce-lev-value:hover { background-color: var(--surface-3); border-bottom-color: currentColor; }
                  .ce-lev-value:focus-visible { outline: 2px solid var(--cat-accent); outline-offset: 2px; }

                  /* Being edited. Same metrics as the readout so the number does
                     not jump on click, and the underline goes solid in the risk
                     colour to say the field is live. */
                  .ce-lev-field {
                    font-family: 'General Sans';
                    font-weight: 700;
                    font-size: 19px;
                    line-height: 1.1;
                    font-variant-numeric: tabular-nums;
                    text-align: right;
                    padding: 0 3px 1px;
                    border: none;
                    border-bottom: 2px solid currentColor;
                    background: transparent;
                    outline: none;
                    border-radius: 0;
                  }
                `}
              </style>

              {/* Label left, value and risk right — the two things the trader
                  reads, on one line, at the size their importance warrants. */}
              <div className="flex items-center justify-between" style={{ marginBottom: 9 }}>
                <span style={{ fontFamily: 'General Sans', fontWeight: 500, fontSize: 13.5, color: 'var(--ink-2, #667085)' }}>
                  Leverage
                </span>
                <span className="ce-lev-kick" key={`lev-kick-${levEditing ? 'edit' : leverageStep}`}>
                  {/* The value is a field, not a label. Nothing said so before,
                      which is why it carries the same dashed underline the rest
                      of the app uses for edit-in-place values. */}
                  {levEditing ? (
                    <input
                      autoFocus
                      type="text"
                      inputMode="numeric"
                      aria-label="Leverage value"
                      value={levDraft}
                      // Digits plus at most one separator. Leverage has no
                      // fractional steps, but the separator has to be ACCEPTED
                      // rather than dropped: deleting it as you type turned
                      // "37.5" into "375", which then clamped to the market max
                      // — a trader asking for 37.5x silently got 100x. It is
                      // accepted here and rounded on commit, so they get 38x.
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9.,]/g, '');
                        const [head, ...rest] = cleaned.split(/[.,]/);
                        setLevDraft(rest.length ? `${head}.${rest.join('')}` : head);
                      }}
                      onFocus={(e) => e.target.select()}
                      onBlur={commitLevEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitLevEdit(); }
                        else if (e.key === 'Escape') { e.preventDefault(); setLevEditing(false); }
                      }}
                      className="ce-lev-field ce-bare"
                      style={{ width: `${Math.max(2, levDraft.length) + 0.5}ch`, color: leverageRiskColor }}
                    />
                  ) : (
                    <span
                      role="button"
                      tabIndex={0}
                      title="Click to type a leverage value"
                      onClick={beginLevEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); beginLevEdit(); }
                      }}
                      className="ce-lev-value"
                      style={{ color: leverageRiskColor }}
                    >
                      <NumberTicker text={`${leverageStep}×`} duration={0.34} stagger={0} startOnView={false} />
                    </span>
                  )}
                  {/* Risk stated in words as well as colour: red/green is the
                      one pair a colour-blind trader cannot separate, and this
                      is the ticket's warning. */}
                  <span
                    style={{
                      fontFamily: 'General Sans', fontWeight: 700, fontSize: 10, letterSpacing: '0.04em',
                      padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
                      color: leverageRiskColor,
                      backgroundImage: `linear-gradient(rgba(${leverageZone.rgb}, 0.16), rgba(${leverageZone.rgb}, 0.16))`,
                    }}
                  >
                    {leverageZone.label.toUpperCase()} RISK
                  </span>
                </span>
              </div>

              <RangeSlider
                value={leverageStep}
                onChange={(v) => { hapticTick(); handleLeverageChange(v); }}
                min={1}
                max={maxLeverage}
                // Whole multiples: leverage has no fractional steps, and every
                // control here — slider, ticks, the handler itself — lands on
                // the same integers.
                step={1}
                // A dot per detent would be 100 dots on a 4px pitch, which
                // reads as a dotted line rather than a scale. Roughly 20 across
                // whatever range this market allows.
                tickEvery={Math.max(1, Math.round((maxLeverage - 1) / 20))}
                // The trough skin, where the deposit's share slider stays
                // plain: leverage runs 1-100 with no labelled stops, so the
                // dot row is the only thing saying how far along the range the
                // handle has travelled.
                variant="trough"
                color={leverageRiskColor}
                ariaLabel="Leverage"
                format={(v) => `${v}×`}
              />

              <div className="flex items-center justify-between" style={{ marginTop: 4 }}>
                {leverageTicks.map((tick) => {
                  const active = leverageStep === tick;
                  return (
                    <button
                      key={tick}
                      type="button"
                      onClick={() => { hapticTap(); handleLeverageChange(tick); }}
                      className="ce-lev-tick"
                      style={{
                        fontWeight: active ? 700 : 500,
                        color: active ? leverageRiskColor : 'var(--ink-3, #98A2B3)',
                      }}
                    >
                      {tick}×
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* The summary is *reference*, so it is recessed rather than raised:
              you read it, you do not press it. The card it replaces was
              `backdrop-blur-xl` over a white/50 gradient — blurring an opaque
              background behind it, which costs a compositor layer per frame
              and renders identically to a flat fill. */}
          <div
            style={{
              background: 'var(--surface-2)',
              boxShadow: 'var(--inset-sm)',
              borderRadius: 16,
              padding: '2px 14px',
              flex: '0 0 auto',
            }}
          >
            <div className="ce-summary">

              {/* Open Price (Limit only) */}
              {tab !== 0 && (
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">Open Price</div>
                  <input
                    type="number"
                    value={openPrice}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (e.target.value === "" || isNaN(val)) {
                        setOpenPrice("");
                      } else {
                        const clamped = Math.min(10000000000000, Math.max(0, val));
                        setOpenPrice(clamped);
                      }
                    }}
                    className="w-32 sm:w-40 rounded-[10px] border border-[#E3E3E3] py-[8px] px-[12px] bg-[#F8F4FCCC] focus:outline-none text-right text-lg font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="10,000.00"
                    min="0"
                    onBlur={() => {
                      if (openPrice === "" || isNaN(parseFloat(openPrice))) {
                        setOpenPrice(0);
                      } else {
                        const numeric = Number(openPrice);
                        const clamped = Math.min(10000000000000, Math.max(0, numeric));
                        if (clamped !== numeric) {
                          setOpenPrice(clamped);
                        }
                      }
                    }}
                  />
                </div>
              )}

              {/* Execution and liquidation, at the same weight as the rest of
                  the ticket. Both were 18px with a coloured glow, which made
                  three competing headlines out of numbers that are reference,
                  not the decision. Liquidation keeps its red and gains a
                  warning glyph, so the one genuinely dangerous number on the
                  ticket does not depend on colour alone to read as dangerous.
                  Position Size has moved up beside the deposit. */}
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">Execution Price</div>
                <span style={{ fontFamily: 'General Sans', fontWeight: 600, fontSize: 14.5, color: 'var(--ink, #101828)' }}>
                  <NumberTicker text={formattedExecutionPrice} prefix="$" duration={0.5} stagger={0.03} />
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">Liquidation Price</div>
                {Number(currentLeverage) <= 1 ? (
                  <span style={{ fontFamily: 'General Sans', fontWeight: 500, fontSize: 14.5, color: 'var(--ink-2, #667085)' }}>No liquidation</span>
                ) : (
                  <span style={{ fontFamily: 'General Sans', fontWeight: 600, fontSize: 14.5, color: liquidationPrice > 0 ? '#C8353A' : 'var(--ink-2, #667085)' }}>
                    {liquidationPrice > 0 && <span aria-hidden="true">⚠ </span>}
                    <NumberTicker text={formattedLiquidationPrice} prefix="$" duration={0.5} stagger={0.03} />
                  </span>
                )}
              </div>
            </div>
          </div>
          

          {/* Progressive disclosure.
              Take-profit, stop-loss and slippage are three cards of controls
              that a plain market order needs none of, and they sat between the
              trader and the submit button on every single order. They are one
              row now, and the row states whether anything inside it is set —
              folding a control away must never hide the fact that it is
              active. */}
          <div style={{ borderRadius: 14, background: 'var(--raise)', boxShadow: 'var(--pillow-sm)', overflow: 'hidden', flex: '0 0 auto' }}>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              aria-expanded={showAdvanced}
              className="ce-square flex items-center justify-between w-full"
              style={{
                padding: '11px 14px',
                background: 'transparent',
                border: 'none',
                borderRadius: 14,
                cursor: 'pointer',
                fontFamily: 'General Sans',
                fontSize: 13.5,
                fontWeight: 500,
                color: 'var(--ink-2, #667085)',
              }}
            >
              <span>
                TP / SL &amp; Slippage
                {(tp || sl) && (
                  <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 600, color: 'var(--cat-accent, #7C2BE8)' }}>
                    {[tp && 'TP', sl && 'SL'].filter(Boolean).join(' · ')} set
                  </span>
                )}
              </span>
              <span aria-hidden="true" style={{ display: 'inline-block', transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 180ms var(--ease)' }}>⌄</span>
            </button>

            {showAdvanced && (
              <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TPSLSection
              isManualTPSL={isManualTPSL}
              setIsManualTPSL={setIsManualTPSL}
              longShort={longShort}
              tp={tp}
              sl={sl}
              tpPercentInput={tpPercentInput}
              slPercentInput={slPercentInput}
              handleTpChange={handleTpChange}
              handleSlChange={handleSlChange}
              handleSlBlur={handleSlBlur}
              handleTpPercentInputChange={handleTpPercentInputChange}
              handleSlPercentInputChange={handleSlPercentInputChange}
              percentTP={getPnLPercent(
                currentPrice || openPrice,
                tp,
                longShort === 0
              )}
              percentSL={getPnLPercent(
                currentPrice || openPrice,
                sl,
                longShort === 0
              )}
              TP_EST={
                longShort === 0
                  ? (Number(tp) - Number(tab === 0 ? currentPrice : openPrice)) *
                    Number(leverage_value)
                  : (Number(tab === 0 ? currentPrice : openPrice) - Number(tp)) *
                    Number(leverage_value)
              }
              SL_EST={
                longShort === 0
                  ? (Number(tab === 0 ? currentPrice : openPrice) - Number(sl)) *
                    Number(leverage_value)
                  : (Number(sl) - Number(tab === 0 ? currentPrice : openPrice)) *
                    Number(leverage_value)
              }
            />
          
            {/* Estimated Slippage */}
          
            <EstimatedSlippage
              expectedPrice={tab === 0 ? currentPrice : openPrice}
              actualPrice={currentPrice}
              slippage={slippage}
              setSlippage={setSlippage}
              isAutoSlippage={isAutoSlippage}
              setIsAutoSlippage={setIsAutoSlippage}
              onSlippageChange={handleSlippageChange}
              openingFee={openingFee}
              closingFee={closingFee}
              isLoadingFees={isLoadingFees}
              dailyBorrowingRate={dailyBorrowingRate}
            />
              </div>
            )}
          </div>

          </div>{/* ── end of the ticket body ──────────────────────────── */}

          {/* Funding, only when it is the next thing to do. This was a
              full-width purple "Get USDC on Nibiru" sitting permanently above
              the submit button whether or not the trader had USDC — two
              primary buttons stacked, with the wrong one on top. */}
          {insufficientBalance && !market?.pocMode && !isMarketDisabled && !isMarketClosed && (
            <button
              type="button"
              onClick={() => {
                bridgeFlowFromConnectRef.current = false;
                setShowUsdcBridgeModal(true);
              }}
              style={{
                width: '100%',
                flex: '0 0 auto',
                padding: '10px 14px',
                borderRadius: 999,
                background: 'var(--cat-accent-soft, rgba(91, 29, 238, .10))',
                border: 0,
                fontFamily: 'General Sans',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--cat-accent, #5B1DEE)',
                cursor: 'pointer',
              }}
            >
              Not enough USDC — get USDC on Nibiru
            </button>
          )}

          {market?.pocMode ? (
            <div
              style={{
                width: '100%',
                flex: '0 0 auto',
                padding: '14px',
                borderRadius: '16px',
                background: 'var(--cat-accent-soft, rgba(91, 29, 238, .10))',
                border: 0,
                textAlign: 'center',
                fontFamily: 'General Sans',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--cat-accent, #5B1DEE)',
              }}
            >
              Market Coming Soon — Preview Mode
            </div>
          ) : isMarketDisabled ? (
            <div
              style={{
                width: '100%',
                flex: '0 0 auto',
                padding: '14px',
                borderRadius: '16px',
                background: 'var(--cat-accent-soft, rgba(91, 29, 238, .10))',
                border: 0,
                textAlign: 'center',
                fontFamily: 'General Sans',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--cat-accent, #5B1DEE)',
              }}
            >
              Market Disabled — New Positions Not Accepted
            </div>
          ) : isMarketClosed ? (
            <div
              style={{
                width: '100%',
                flex: '0 0 auto',
                padding: '14px',
                borderRadius: '16px',
                background: 'var(--cat-accent-soft, rgba(91, 29, 238, .10))',
                border: 0,
                textAlign: 'center',
                fontFamily: 'General Sans',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--cat-accent, #5B1DEE)',
              }}
            >
              {market?.category === 'commodities'
                ? 'Market Closed — Opens Sun–Fri 6 PM ET'
                : 'Market Closed — Opens Mon–Fri 9:30 AM ET'}
            </div>
          ) : (
            /* One button, stating the order it is about to place, so the last
               thing read before committing is the trade rather than the word
               "Market". Blocked orders name what is blocking them instead of
               failing on click. */
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => { if (canSubmit) setConfirming(true); }}
              className={`ce-submit ${!canSubmit ? 'is-off' : longShort === 0 ? 'is-long' : 'is-short'}`}
              style={{
                width: '100%',
                height: 50,
                borderRadius: 16,
                border: 'none',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                fontFamily: 'General Sans',
                fontWeight: 600,
                fontSize: 15.5,
                flex: '0 0 auto',
                transition: 'transform var(--dur-fast) var(--ease), box-shadow var(--dur-base) var(--ease)',
              }}
            >
              {submitLabel}
            </button>
          )}

          {/* The confirmation dialog. Rendered from the ticket but owning the
              screen while it is up — see OrderConfirmModal. */}
          <OrderConfirmModal
            open={confirming}
            onClose={() => setConfirming(false)}
            onConfirm={() => { setConfirming(false); handleOpenTrade(); }}
            side={longShort}
            orderType={tab}
            pair={market?.symbol ? `${market.symbol}/USDC` : "—"}
            marketName={market?.name}
            deposit={clampedAmount}
            leverage={clampedLeverage}
            positionSize={positionSize}
            price={tab === 0 ? formattedExecutionPrice : formatNumber(String(openPrice || 0))}
            liquidationPrice={formattedLiquidationPrice}
            openingFee={openingFee}
            closingFee={closingFee}
            isLoadingFees={isLoadingFees}
            dailyBorrowingRate={dailyBorrowingRate}
            slippage={slippage}
            isAutoSlippage={isAutoSlippage}
            tp={tp}
            sl={sl}
            balance={tokenBalance}
            formatNumber={formatNumber}
          />

          {/* Bridge helper modals */}
          <Modal
            open={showUsdcBridgeModal}
            onClose={() => {
              setShowUsdcBridgeModal(false);
              if (bridgeFlowFromConnectRef.current) {
                setShowNibiBridgeModal(true);
              }
            }}
            center
            closeOnOverlayClick={true}
            closeOnEsc={true}
            classNames={{
              modal:
                "relative max-w-[420px] w-[95vw] md:max-w-[420px] md:w-[95vw] bg-white p-3 md:p-6 rounded-[16px] shadow-[0_12px_30px_rgba(15,23,42,0.18)] border border-gray-100 mx-auto max-h-[90vh] overflow-y-auto",
            }}
          >
            <div className="space-y-2 md:space-y-4">
              <h2 className="text-base md:text-xl font-semibold text-gray-900 flex items-center gap-1.5 md:gap-2">
                <NibiruIcon className="w-4 h-4 md:w-5 md:h-5" />
                <span>Get USDC on Nibiru</span>
              </h2>
              <p className="text-xs md:text-sm text-gray-600 leading-tight md:leading-normal">
                Follow these steps to bridge USDC to Nibiru using the official Nibiru Bridge.
              </p>
              <ol className="space-y-1.5 md:space-y-3 text-xs md:text-sm text-gray-700 list-decimal list-inside leading-relaxed">
                <li>
                  Click <span className="font-semibold">"Open Nibiru Bridge"</span> below. This opens the Nibiru bridge in a new tab.
                </li>
                <li>
                  In the bridge, set <span className="font-semibold">Supported Asset</span> to <span className="font-semibold">USD Coin (USDC.e)</span>.
                </li>
                <li>
                  Choose your <span className="font-semibold">Source Chain</span> (where your USDC is) and set the <span className="font-semibold">Destination Chain</span> to <span className="font-semibold">Nibiru</span>.
                </li>
                <li>
                  Enter the <span className="font-semibold">amount</span> of USDC you want to send and your <span className="font-semibold">Nibiru receiving address</span> (your wallet address).
                </li>
                <li>
                  Click <span className="font-semibold">"Bridge"</span>, confirm the transaction in your wallet, and wait 5–10 minutes for funds to arrive on Nibiru.
                </li>
                <li>
                  Once USDC arrives on Nibiru, you’re ready to trade on <span className="font-semibold">CodedEState</span>.
                </li>
              </ol>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-3 text-[10px] md:text-xs text-blue-800">
                <p className="font-semibold mb-0.5 md:mb-1">💡 Tip:</p>
                <p className="leading-tight">
                  Make sure you have a small amount of gas on your source chain to pay for the bridge transaction.<br/>
                  Nibiru Bridge also provides free NIBI tokens for gas, so you’re covered once your funds arrive.
                </p>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2 pt-0.5 md:pt-1">
                <input
                  id="bridge-hide-on-connect-usdc"
                  type="checkbox"
                  checked={hideBridgeOnConnect}
                  onChange={handleToggleHideBridgeOnConnect}
                  className="h-3.5 w-3.5 md:h-4 md:w-4 rounded border-gray-300 text-[#5B1DEE] focus:ring-[#5B1DEE] flex-shrink-0"
                />
                <label
                  htmlFor="bridge-hide-on-connect-usdc"
                  className="text-[10px] md:text-xs text-gray-600 cursor-pointer leading-tight"
                >
                  Do not show this again when I connect my wallet
                </label>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-1 md:pt-2">
                <PurpleButton
                  text="Open Nibiru Bridge"
                  className="w-full sm:w-auto text-xs md:text-sm px-3 md:px-[14px] py-2 md:py-[8px]"
                  onClick={() => {
                    window.open("https://app.nibiru.fi/bridge", "_blank", "noopener,noreferrer");
                  }}
                />
                {/* <WhiteButton
                  text="Next: Get NIBI Gas"
                  className="w-full sm:w-auto text-xs md:text-sm font-medium text-gray-700 px-3 md:px-[14px] py-2 md:py-[8px]"
                  onClick={() => {
                    setShowUsdcBridgeModal(false);
                    if (bridgeFlowFromConnectRef.current) {
                      setShowNibiBridgeModal(true);
                    }
                  }}
                /> */}
              </div>
            </div>
          </Modal>

          {/* <Modal
            open={showNibiBridgeModal}
            onClose={() => setShowNibiBridgeModal(false)}
            center
            closeOnOverlayClick={true}
            closeOnEsc={true}
            classNames={{
              modal:
                "relative max-w-[420px] w-[95vw] md:max-w-[420px] md:w-[95vw] bg-white p-3 md:p-6 rounded-[16px] shadow-[0_12px_30px_rgba(15,23,42,0.18)] border border-gray-100 mx-auto max-h-[90vh] overflow-y-auto",
            }}
          >
            <div className="space-y-2 md:space-y-4">
              <h2 className="text-base md:text-xl font-semibold text-gray-900 flex items-center gap-1.5 md:gap-2">
                <NibiruIcon className="w-4 h-4 md:w-5 md:h-5" />
                <span>Get NIBI Gas on Nibiru</span>
              </h2>
              <p className="text-xs md:text-sm text-gray-600 leading-tight md:leading-normal">
                You need NIBI tokens to pay for gas fees when trading. Here are two easy ways to get NIBI:
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 md:p-3 mb-2 md:mb-4">
                <p className="text-xs md:text-sm font-semibold text-green-800 mb-1 md:mb-2">✅ Easiest Option:</p>
                <p className="text-[10px] md:text-xs text-green-700 leading-tight">
                  When bridging USDC through Stargate, you can automatically add up to 40 NIBI tokens for gas. Just click "Add more gas" if you see the warning message. This way you get both USDC and NIBI in one transaction!
                </p>
              </div>

              <p className="text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">Or bridge NIBI separately:</p>
              <ol className="space-y-1 md:space-y-2 text-xs md:text-sm text-gray-700 list-decimal list-inside leading-relaxed">
                <li>
                  Click <span className="font-semibold">"Open Stargate Bridge"</span> below.
                </li>
                <li>
                  <span className="font-semibold">Select your source chain</span> - Choose where you have NIBI tokens.
                </li>
                <li>
                  <span className="font-semibold">Select NIBI</span> as the token to bridge.
                </li>
                <li>
                  <span className="font-semibold">Set Nibiru</span> as the destination and enter a small amount (0.1-1 NIBI is enough for many transactions).
                </li>
                <li>
                  Review and confirm the transaction. Once it completes, you'll have NIBI for gas fees.
                </li>
              </ol>
              <div className="flex items-center gap-1.5 md:gap-2 pt-0.5 md:pt-1">
                <input
                  id="bridge-hide-on-connect-nibi"
                  type="checkbox"
                  checked={hideBridgeOnConnect}
                  onChange={handleToggleHideBridgeOnConnect}
                  className="h-3.5 w-3.5 md:h-4 md:w-4 rounded border-gray-300 text-[#5B1DEE] focus:ring-[#5B1DEE] flex-shrink-0"
                />
                <label
                  htmlFor="bridge-hide-on-connect-nibi"
                  className="text-[10px] md:text-xs text-gray-600 cursor-pointer leading-tight"
                >
                  Do not show this again when I connect my wallet
                </label>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-1 md:pt-2">
                <BlackButton
                  text="Open Stargate Bridge"
                  className="w-full sm:w-auto text-xs md:text-sm px-3 md:px-[14px] py-2 md:py-[8px]"
                  onClick={() => {
                    window.open("https://stargate.finance/?dstChain=nibiru&dstToken=0x0829F361A05D993d5CEb035cA6DF3446b060970b", "_blank", "noopener,noreferrer");
                  }}
                />
                <WhiteButton
                  text="Close"
                  className="w-full sm:w-auto text-xs md:text-sm font-medium text-gray-700 px-3 md:px-[14px] py-2 md:py-[8px]"
                  onClick={() => setShowNibiBridgeModal(false)}
                />
              </div>
            </div>
          </Modal> */}

        </div>
      </div>
    </>
  );
};

export default TradingForm;